import React, { useState, useEffect } from "react";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  paymentData: {
    discount: string;
    ReceivedAmount: string;
    paymentMethod: string;
  };
  onPaymentDataChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessPayment: () => void;
  isProcessing: boolean;
}

const Row: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  checkbox?: boolean;
  checked?: boolean;
  onCheck?: () => void;
}> = ({ label, value, bold, checkbox, checked, onCheck }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex items-center gap-2">
      {checkbox && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onCheck}
          className="w-4 h-4 rounded border-gray-300 accent-blue-600"
        />
      )}
      <span
        className={`text-sm ${bold ? "text-gray-900 font-medium" : "text-gray-600"}`}
      >
        {label}
      </span>
    </div>
    <span
      className={`text-sm ${bold ? "text-gray-900 font-semibold" : "text-gray-700"}`}
    >
      {value}
    </span>
  </div>
);

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  order,
  paymentData,
  onPaymentDataChange,
  onProcessPayment,
  isProcessing,
}) => {
  const [localReceivedAmount, setLocalReceivedAmount] = useState("0");
  const [error, setError] = useState("");
  const [includeOutstanding, setIncludeOutstanding] = useState(false);

  useEffect(() => {
    setLocalReceivedAmount(paymentData.ReceivedAmount || "0");
  }, [paymentData.ReceivedAmount]);

  const methodLower = (paymentData.paymentMethod || "").toLowerCase().trim();
  const isBuyOnCredit =
    methodLower === "buy on credit" ||
    methodLower === "paylater" ||
    methodLower === "pay late";

  // Get the current order (first order)
  const currentOrder = order?.orders?.[0];

  // Calculate totals from all orders if multiple exist
  const totalOrderAmount =
    order?.orders?.reduce((sum, o) => sum + Number(o.orderAmount || 0), 0) || 0;
  const totalDiscount =
    order?.orders?.reduce((sum, o) => sum + Number(o.discount || 0), 0) || 0;
  const totalFinalAmount =
    order?.orders?.reduce((sum, o) => sum + Number(o.finalAmount || 0), 0) || 0;

  // Calculate total received from payment history (all payments)
  const totalReceivedFromHistory =
    order?.paymentHistory?.reduce(
      (sum, p) => sum + Number(p.receivedAmount || 0),
      0,
    ) || 0;

  // Get remaining amount from the order (this is the actual remaining amount from backend)
  const orderRemainingAmount = Number(currentOrder?.remainingAmount || 0);

  // The final amount is the total order amount minus discount
  const finalAmount = totalOrderAmount - totalDiscount;

  // The old remaining amount is what's still owed (from backend)
  const oldRemaining = orderRemainingAmount;

  const customerOutstanding = Number(order?.totalOutstandingBalance || 0);
  const totalInclOutstanding = oldRemaining + customerOutstanding;

  const enteredAmount = parseFloat(localReceivedAmount || "0");
  const newRemaining = Math.max(oldRemaining - enteredAmount, 0);
  const isValidAmount =
    enteredAmount >= 0 &&
    enteredAmount <= oldRemaining &&
    !isNaN(enteredAmount);

  const customerName =
    [
      order?.customerDetails?.firstName,
      order?.customerDetails?.middleName,
      order?.customerDetails?.lastName,
    ]
      .filter(Boolean)
      .join(" ") || "N/A";

  const handleFocus = () => {
    if (localReceivedAmount === "0") setLocalReceivedAmount("");
    setError("");
  };

  const handleBlur = () => {
    if (localReceivedAmount.trim() === "") {
      setLocalReceivedAmount("0");
      const syntheticEvent = {
        target: { name: "ReceivedAmount", value: "0" },
      } as React.ChangeEvent<HTMLInputElement>;
      onPaymentDataChange(syntheticEvent);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setLocalReceivedAmount("");
      setError("");
      return;
    }

    const enteredVal = parseFloat(value);

    if (isNaN(enteredVal)) {
      setLocalReceivedAmount(value);
      setError("Please enter a valid number");
      return;
    }

    if (enteredVal > oldRemaining) {
      setError(`Amount cannot exceed ${oldRemaining.toFixed(2)} QAR`);
      setLocalReceivedAmount(value);
    } else {
      setError("");
      setLocalReceivedAmount(value);
      onPaymentDataChange(e);
    }
  };

  if (!open) return null;

  return (
    <ModalWrapper
      title="Payment Summary"
      onClose={onClose}
      onBack={onClose}
      showCloseButton
      showBackButton
      width="max-w-md"
    >
      <div className="flex flex-col bg-white rounded-2xl max-h-[85vh]">
        <div className="px-5 py-4 overflow-y-auto scrollbar-hide">
          {/* Customer card */}
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-3 mb-4">
            <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-gray-200 flex items-center justify-center">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-500"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                Customer: {customerName}
              </div>
              <div className="text-sm text-gray-600 capitalize">
                Payment: {paymentData.paymentMethod}
              </div>
              <div className="text-xs text-gray-400">
                Order ID: {currentOrder?.orderId || "N/A"}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">
              Order Summary
            </h3>
            <div className="pt-1">
              <Row
                label="Order Amount"
                value={`${totalOrderAmount.toFixed(2)} QAR`}
              />
              <Row
                label="Discount"
                value={`- ${totalDiscount.toFixed(2)} QAR`}
              />
              <Row
                label="Total Received"
                value={`${totalReceivedFromHistory.toFixed(2)} QAR`}
                bold
              />
              <Row
                label="Remaining Amount"
                value={`${oldRemaining.toFixed(2)} QAR`}
                bold
              />
            </div>
          </div>

          {/* Payment Status */}
          <div>
            <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">
              Payment Status
            </h3>
            <div className="pt-1">
              {isBuyOnCredit ? (
                <>
                  <Row
                    label="Order Remaining"
                    value={`${oldRemaining.toFixed(2)} QAR`}
                  />
                  {customerOutstanding > 0 && (
                    <Row
                      label="Customer Outstanding"
                      value={`${customerOutstanding.toFixed(2)} QAR`}
                    />
                  )}
                  <Row
                    label="Total (Incl Outstanding)"
                    value={`${totalInclOutstanding.toFixed(2)} QAR`}
                    bold
                  />
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">
                      Received Amount
                    </span>
                    <div className="flex flex-col items-end">
                      <input
                        name="ReceivedAmount"
                        type="number"
                        value={localReceivedAmount}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        min={0}
                        max={oldRemaining}
                        step="0.01"
                        placeholder="0"
                        style={{ textAlign: "right" }}
                        className={`w-28 h-9 rounded-md border px-2.5 text-right text-sm focus:outline-none ${
                          error
                            ? "border-red-400 focus:border-red-500"
                            : "border-gray-300 focus:border-blue-400"
                        }`}
                      />
                      {error && (
                        <span className="text-xs text-red-500 mt-1">
                          {error}
                        </span>
                      )}
                    </div>
                  </div>

                  <Row
                    label="New Remaining Amount"
                    value={`${newRemaining.toFixed(2)} QAR`}
                    bold
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="w-32 h-10 rounded-full border-2 border-blue-500 text-blue-600 font-semibold text-sm hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onProcessPayment}
            disabled={
              isProcessing ||
              (!isBuyOnCredit &&
                (!isValidAmount ||
                  localReceivedAmount.trim() === "" ||
                  parseFloat(localReceivedAmount) <= 0))
            }
            className="w-36 h-10 rounded-full bg-blue-500 text-white font-semibold text-sm hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? "Processing..." : "Complete"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default PaymentDialog;
